import { version } from "../../package.json";
import getAdverts from "./getAdverts";
import triggerBeacon from "./triggerBeacon";
import buildNoCallParamFromOptions from "./buildParameters/buildNoCallParamFromOptions";
import buildParamsFromApp from "./buildParameters/buildParamsFromApp";
import buildParamsFromIdentity from "./buildParameters/buildParamsFromIdentity";
import buildParamsFromWebContext from "./buildParameters/buildParamsFromWebContext";
import validateParameters from "./validateParameters";
import getDisplayAdPlacement from "./getDisplayAdPlacement";
import getFeaturesConfig from "./getFeaturesConfig";
import isValidCountry from "./validateParameters/isValidCountry";
import { PLACEMENT } from "../constants";
import validateAndNormaliseWebContext from "./validateAndNormaliseWebContext";

/**
 * Adverts SDK function
 * @function
 * @param {Object} sdkParameters
 * @param {Object} sdkParameters.identityDelegatePromise a promise returning the identity sdk
 * @param {Object} sdkParameters.webContextDelegate the webcontext from the consumer
 * @param {Object} sdkParameters.webContextDelegate.browseCountry
 * @param {Object} sdkParameters.production whether or not the sdk is in production mode
 * @param {Object} sdkParameters.featuresDelegatePromise a promise returning the features sdk
 */
const sdk = (sdkParameters) => {
  const {
    webContextDelegate,
    identityDelegatePromise: identityPromise,
    featuresDelegatePromise: featuresPromise,
    production
  } = sdkParameters;

  const webContext = validateAndNormaliseWebContext(webContextDelegate);

  const { browseCountry, platform } = webContext;

  const validCountry = isValidCountry(browseCountry, production);

  /**
   * @module sdkInstance
   **/
  return {
    /**
     * @property {string} version version of the SDK
     */
    version,
    /**
     * @property {function} getAdverts function to return ads from the criteo api
     * @param {Object} options configuration options that assist in parameter generation but are not themselves sent to the api
     * @param {String} options.app a page/microfrontend related concern used to derive page oriented api parameters
     * @param {String} options.placement an advert selector that relates to the criteo placement key in the advert response
     * @param {Boolean} options.requestSponsoredProducts a boolean that signifies if sponsored product adverts are desired in the response
     * @param {Boolean} options.requestDisplayAdverts a boolean that signifies if display adverts are desired in the response
     * @param {Boolean} options.breakCache a boolean that forces the sdk to ignore response cache and make a fresh request
     * @param {Object} parameters user provided parameters sent directly to the api
     */
    getAdverts: async ({ options, parameters }) => {
      if (!validCountry) return {};

      const {
        app,
        placement,
        requestSponsoredProducts,
        requestDisplayAdverts,
        breakCache
      } = options;

      const { enableAPICalls, enableDisplayAdverts } = await getFeaturesConfig(
        app,
        browseCountry,
        featuresPromise,
        production
      );

      if (!enableAPICalls) return {};

      const noCallParameter = buildNoCallParamFromOptions(
        requestSponsoredProducts,
        requestDisplayAdverts
      );

      validateParameters(app, noCallParameter, parameters);

      const personalisationParameters = await buildParamsFromIdentity(
        identityPromise
      );

      const environmentParameters = buildParamsFromWebContext(
        webContext,
        production
      );

      const appParameters = buildParamsFromApp(app, platform);

      const displayAdsPlacement = getDisplayAdPlacement(
        app,
        platform,
        placement
      );

      const sdkParameters = {
        ...personalisationParameters,
        ...environmentParameters,
        ...appParameters,
        nocall: noCallParameter
      };

      const adverts = await getAdverts(
        sdkParameters,
        parameters,
        displayAdsPlacement,
        breakCache
      );

      if (enableDisplayAdverts || placement === PLACEMENT.SPONSORED_PRODUCTS) {
        return adverts;
      }

      return {};
    },
    /**
     * @property {function} triggerBeacon function to trigger beacons on behalf of the user
     * @param {Object} options configuration options that assist in locating and triggering specific beacons
     * @param {String} options.app a page/microfrontend related concern used to derive page oriented api parameters
     * @param {String} options.placement an advert selector that relates to the criteo placement key in the advert response
     * @param {String} options.beacon a beacon type constant that relates to real beacon types in the criteo response
     */
    triggerBeacon: (options) => {
      const { app, placement, beacon } = options;

      triggerBeacon(app, placement, beacon, platform);
    },
    /**
     * @property {function} isGoogleAdvertsEnabled function that returns a boolean if google ads should be enabled or not
     * @param {Object} options configuration options that assist in the localisation of the app for feature toggling
     * @param {String} options.app a page/microfrontend related concern used to derive page oriented api parameters
     */
    isGoogleAdvertsEnabled: async (options) => {
      const { app } = options;

      const { enableGoogleAdverts } = await getFeaturesConfig(
        app,
        browseCountry,
        featuresPromise,
        production
      );

      return enableGoogleAdverts;
    }
  };
};

export default sdk;
